import _ from "lodash";
import moment from "moment";

import { getBulkOperationResult } from "@tickets/helpers/getBulkOperationResult";
import { openDeferModal } from "@tickets/helpers/openDeferPopup";
import { getTicketId } from "@tickets/helpers/getTicketId";
import {
	addToBalance,
	resetPlayerPaymentInfo,
	savePlayerPaymentInfo,
	setLastMoneyInputType,
	setPlayerPaymentInfo,
	setTerminalDepositMode,
} from "@app/dev/actions/terminal";
import { payUserBet, verifyUser } from "@app/dev/helpers/verification";
import { store } from "@app/dev/index/terminalApp";
import { POPUPS } from "@common/constants/popups";
import { TicketTypes } from "@tickets/constants";
import Config from "@skin_config";
import PaymentsService, { sessionAndTerminalStateMap, SessionBalanceStates } from "@app/dev/services/payments";
import ToastWrapper from "@common/helpers/toastWrapper";
import { getPopupDispatchers } from "@app/dev/contexts/popup";
import { TerminalDepositModesEnum } from "@app/dev/constants/enums/terminalDepositModes";
import { getAuthData } from "@common/helpers/auth";
import { isMultiDepositModeEnabled } from "@app/dev/helpers/multiDepositMode";
import { fetchRequest } from "@common/helpers/util/requestMiddleware";

const SPORT_TICKET = 1;
const CASINO_TICKET = 2;
const CASHOUT_TICKET = 3;

// const getPayRequestModel = (ticket) => {
//   if (!ticket.dataFromSwarm && !ticket.dataFromCasino) return [];

//   if (ticket.ticketType === TicketTypes.SPORT) {
//     const taxAmount = ticket.dataFromSwarm.tax_amount || ticket.dataFromSwarm.possible_tax_amount;
//     return [
//       {
//         barcode: ticket.dataFromSwarm.barcode,
//         amount: ticket.dataFromSwarm.win_amount,
//         tax_amount: taxAmount,
//         ticket_type: SPORT_TICKET,
//         with_credientals: ticket.dataFromSwarm.win_amount > Config.tickets.minWinAmountForIdentification,
//       },
//     ];
//   }

//   if (ticket.ticketType === TicketTypes.DEPOSIT) {
//     return [
//       {
//         barcode: `${ticket.dataFromSwarm.barcode}`.slice(0, -1),
//         amount: ticket.amount,
//         tax_amount: 0,
//         ticket_type: CASHOUT_TICKET,
//         with_credientals: false,
//       },
//     ];
//   }

//   if (ticket.ticketType === TicketTypes.CASINO) {
//     return [
//       {
//         barcode: ticket.dataFromCasino?.CasinoTransactionId,
//         tax_amount: ticket.dataFromCasino.TotalTaxAmount, // maybe unusable for Igor
//         ticket_type: CASINO_TICKET,
//         platform_transaction_id: ticket.dataFromCasino?.PlatformTransactionId,
//         amount: ticket.dataFromCasino.TotalWinAmount - ticket.dataFromCasino.TotalTaxAmount,
//         with_credientals: ticket.dataFromCasino.TotalWinAmount > Config.tickets.minWinAmountForIdentification,
//       },
//     ];
//   }
// };

export const getTicketBarcode = ticket => {
	const isFullTicket = ticket.originalBarcode.startsWith("1") && ticket.originalBarcode.length === 20;

	if (isFullTicket) return ticket.originalBarcode;

	const isDeposit = ticket.originalBarcode.startsWith('9');

	if (isDeposit) return ticket.originalBarcode.substring(1);

	return ticket.barcode || ticket.dataFromSwarm?.barcode || ticket.dataFromCasino?.CasinoTransactionId;
};

const getPayRequestModel = (tickets, terminalActivePlayerId) => {
	const { depositMode } = store.getState().terminal;

	const ticketsList = [];
	for (const ticket of tickets) {
		if (!ticket.dataFromSwarm) continue;

		if (ticket.ticketType === TicketTypes.SPORT) {
			const taxAmount = ticket.dataFromSwarm.tax_amount || 0;

			ticketsList.push({
				barcode: getTicketBarcode(ticket),
				playerData: ticket.playerData,
				amount: ticket.dataFromSwarm.win_amount,
				bet_amount: ticket.dataFromSwarm.amount,
				tax_amount: taxAmount,
				ticket_type: SPORT_TICKET,

				betshop_id: ticket.dataFromSwarm.betshop_id,
				cash_desk_id: ticket.dataFromSwarm.cash_desk_id,
				cash_desk_name: ticket.dataFromSwarm.cash_desk_name,
				created: ticket.dataFromSwarm.created,
				type_name: ticket.dataFromSwarm.type_name,

				with_credientals: [
					TerminalDepositModesEnum.Cash,
					TerminalDepositModesEnum.Ticket,
				].includes(depositMode)
					? ticket.dataFromSwarm.win_amount - taxAmount >
					Config.tickets.minWinAmountForIdentification
					: !!ticket.playerData?.PersonalId &&
					terminalActivePlayerId &&
					terminalActivePlayerId !== ticket.playerData?.PersonalId,
			});
		}

		if (ticket.ticketType === TicketTypes.DEPOSIT) {
			console.log(ticket, "dat");
			ticketsList.push({
				barcode: `${ticket.dataFromSwarm.barcode}`.slice(0, -1),
				amount: ticket.dataFromSwarm.amount,
				bet_amount: ticket.dataFromSwarm.amount,
				cash_desk_id: ticket.dataFromSwarm.created_by_cash_desk_id,
				created: ticket.dataFromSwarm.date,
				tax_amount: 0,
				ticket_type: CASHOUT_TICKET,
				with_credientals: false,
			});
		}

		if (ticket.ticketType === TicketTypes.CASINO) {
			const totalWinAmount = ticket.dataFromSwarm.WinAmount;
			ticketsList.push({
				playerData: ticket.playerData,
				id: ticket.dataFromSwarm.Id,
				barcode: +ticket.dataFromSwarm.ExternalId || ticket.dataFromCasino?.CasinoTransactionId,
				tax_amount: ticket.dataFromCasino?.TotalTaxAmount || 0,
				ticket_type: CASINO_TICKET,
				platform_transaction_id:
					ticket.dataFromSwarm.WinId || ticket.dataFromCasino?.PlatformTransactionId,
				amount: totalWinAmount,
				bet_amount: ticket.dataFromSwarm.Amount,

				cash_desk_id: ticket.dataFromSwarm.CashDeskId,
				cash_desk_name: ticket.dataFromSwarm.CashdeskName,
				created: ticket.dataFromSwarm.Created,

				with_credientals: [
					TerminalDepositModesEnum.Cash,
					TerminalDepositModesEnum.Ticket,
				].includes(depositMode)
					? totalWinAmount > Config.tickets.minWinAmountForIdentification
					: !!ticket.playerData,
			});
		}
	}

	return ticketsList;
};

const getPlayersDataRequestModel = payableTickets =>
	payableTickets.map(ticket => ({
		betId: ticket.id,
		source: ticket.ticket_type,
	}));

const getPayTicketsTotalAmount = (payResult, payTickets) => {
	let amountOfTickets = 0;

	for (const ticketResult of payResult) {
		if (ticketResult.Result?.Succeeded) {
			const barcode = +ticketResult.Result.Data;
			const ticketData = payTickets.find(ticket => +ticket.barcode === barcode);

			amountOfTickets += ticketData.amount - ticketData.tax_amount;
		}
	}

	return amountOfTickets;
};

const updateTerminalDepositMode = async (newState, playerInfo) => {
	console.log(newState, playerInfo, "inside");
	const { tid } = getAuthData();
	const data = playerInfo || PaymentsService.getSessionBalanceEmptyState();
	await PaymentsService.setSessionBalanceState({ ...data, SessionBalanceState: newState });

	const state = sessionAndTerminalStateMap[newState];

	store.dispatch(setLastMoneyInputType(state));
	store.dispatch(setTerminalDepositMode(state));
	if (!playerInfo) {
		store.dispatch(resetPlayerPaymentInfo());
		await fetchRequest(
			Config.casino.middlewareUrl,
			`api/CashdeskDetails/RemoveDetailsByCashdeskId/${tid}?token=${Config.casino.middlewareAuthToken}`,
			"DELETE"
		);
	} else await PaymentsService.setPlayerInfo(data);
};

const getPayedTicketsAmount = () => +sessionStorage.getItem("payedTicketsAmount");

const updatePayedTicketsAmount = async (amount, ticketPlayerData) => {
	const currentAmount = getPayedTicketsAmount();
	const newAmount = currentAmount + amount;
	sessionStorage.setItem("payedTicketsAmount", newAmount);
	const { playerInfo } = store.getState().terminal;

	if (newAmount > Config.main.amountToSwitchToCashMode) {
		updateTerminalDepositMode(SessionBalanceStates.Cash, null);
		store.dispatch(savePlayerPaymentInfo(playerInfo?.PersonalId ? playerInfo : ticketPlayerData)); // save player info to recover and switch to netural mode when it will be less than amountToSwitchToCashMode, just in case when we have player info ()
		sessionStorage.setItem("payedTicketsAmount", 0);
	}
};

export const payTicket = async tickets => {
	const { playerInfo: playerInfoState, depositMode, authData } = store.getState().terminal;
	const terminalActivePlayerId = playerInfoState?.PersonalId;
	const hasBetTicket = tickets.some(ticket =>
		[TicketTypes.SPORT, TicketTypes.CASINO].includes(ticket.ticketType)
	);

	if (hasBetTicket && Config.main.disableTitoBet) {
		ToastWrapper("error", "Please pay this ticket at the cashdesk");
		return [];
	}

	const payRequestModel = getPayRequestModel(tickets, terminalActivePlayerId) || [];

	const firstTicketPlayerData = tickets.find(ticket => ticket.playerData)?.playerData;

	if (terminalActivePlayerId) {
		const otherPlayersTickets = tickets.filter(
			ticket =>
				ticket.playerData?.PersonalId &&
				ticket.playerData?.PersonalId !== terminalActivePlayerId
		);

		if (otherPlayersTickets.length) {
			ToastWrapper(
				"error",
				`You can't pay other players tickets (${otherPlayersTickets
					.map(ticket => ticket.id)
					.join(", ")})`
			);
			return [];
		}
	} else if (
		payRequestModel.some(
			ticket =>
				ticket.playerData?.PersonalId &&
				ticket.playerData?.PersonalId !== firstTicketPlayerData?.PersonalId
		)
	) {
		ToastWrapper("error", `You can't pay different players tickets`);
		return [];
	}

	const playerInfo = {
		Name: firstTicketPlayerData?.Name,
		Surname: firstTicketPlayerData?.Surname,
		CNP: firstTicketPlayerData?.PersonalId,
		..._.omit(firstTicketPlayerData || {}, ["CashdeskId"]),
	};

	const needToPassWinIdentification =
		(Config.tickets.enableWinIdentification &&
			payRequestModel.some(item => item.with_credientals) &&
			(!playerInfo || Object.values(playerInfo).some(item => !item))) ||
		((!!firstTicketPlayerData?.PersonalId || terminalActivePlayerId) &&
			payRequestModel.some(ticket => !ticket.playerData));
	const userData =
		needToPassWinIdentification &&
	//	await openDeferModal(POPUPS.WIN_IDENTIFICATION, { player: !!firstTicketPlayerData?.PersonalId ? playerInfo : null })
	 (await openDeferModal(POPUPS.CNP_VERIFICATION, { player: !!firstTicketPlayerData?.PersonalId ? playerInfo : null }));

	const needToPassVerification =
		Config.verification?.enabled &&
		Config.verification?.payerValidation &&
		tickets.some(ticket => ticket.dataFromSwarm?.member_id);

	const verificationData = needToPassVerification ? await verifyUser() : null;

	if (needToPassVerification) {
		if (!verificationData?.success) return [];

		const isValidPayer = checkPayingUser(tickets, verificationData);

		if (!isValidPayer) return [];
	}

	if (needToPassWinIdentification && _.isEmpty(userData)) return [];

	if (!_.isEmpty(payRequestModel)) {
		const currentAmount = getPayedTicketsAmount();
		const payableTicketsAmount = payRequestModel.reduce(
			(acc, curr) => (acc += curr.amount - curr.tax_amount),
			0
		);

		if (payableTicketsAmount + currentAmount > Config.main.amountToSwitchToCashMode) {
			const isConfirmed = await openDeferModal(POPUPS.CONFIRMATION_MODAL, {
				title: "Pay Ticket",
				icon: "ios-currency",
				okButtonText: "Confirm",
				message: "Terminal will be switched to Cash Mode and you won't be able to withdraw the rest amount with OKTO if you pay this ticket,. Are you sure you want to continue?",
			});

			if (!isConfirmed) return [];
		}

		store.dispatch(
			addToBalance({
				tickets: payRequestModel.map(ticket => ({
					..._.omit(ticket, ["id", "playerData"]),
					with_credientals: !!(userData || playerInfo),
				})),
				user_data: userData || playerInfo,
			})
		);
	}

	const resultList = await getBulkOperationResult(tickets.length);

	if (verificationData?.success) sendPayedTicketsToBIS(verificationData.data, resultList, tickets);

	if (isMultiDepositModeEnabled()) {
		const ticketsAmount = getPayTicketsTotalAmount(resultList, payRequestModel);
		if (
			firstTicketPlayerData?.PersonalId &&
			!playerInfoState.Name &&
			depositMode !== TerminalDepositModesEnum.Cash &&
			ticketsAmount <= Config.main.amountToSwitchToCashMode
		) {
			await updateTerminalDepositMode(SessionBalanceStates.Neutral, {
				...firstTicketPlayerData,
				CashdeskId: +authData?.tid,
			}); // just sending player info to backend to have okto cashout option, without changing terminal deposit mode
		}

		updatePayedTicketsAmount(ticketsAmount, {
			...(firstTicketPlayerData || {}),
			CashdeskId: +authData?.tid,
		});
	}

	return resultList;
};

export const checkPayingUser = (tickets, verificationData) => {
	const isValidPayer = tickets.every(ticket => {
		const { member_id } = ticket.dataFromSwarm || {};
		return !member_id || member_id.toString() === verificationData.data.id?.toString();
	});

	if (!isValidPayer) ToastWrapper("error", "Cannot pay this bet; it belongs to someone else.");

	return isValidPayer;
};

const sendPayedTicketsToBIS = (user, resultList, ticketList) => {
	const requests = resultList
		.map(async result => {
			if (!result?.Result?.Succeeded) return null;

			const barcode = result?.Result?.Data;
			if (!barcode) return null;

			const currentTicket = ticketList.find(ticket => +getTicketBarcode(ticket) === +barcode);

			const res = await payUserBet(user, currentTicket.dataFromSwarm);

			res.errorMessage && ToastWrapper("error", res.errorMessage);

			return res;
		})
		.filter(Boolean);

	return Promise.allSettled(requests);
};

export const isTicketPayPeriodExpired = ticket => {
	const { openPopup } = getPopupDispatchers();
	const ticketDate = ticket?.dataFromSwarm?.calc_date || ticket?.dataFromSwarm?.Created;
	const isTicketPayPeriodExpired =
		moment().diff(moment(ticketDate), "days") > Config.main.ticketPayoutExpirationDays;

	if (isTicketPayPeriodExpired)
		openPopup(POPUPS.ERROR_POPUP, {
			errorMessage: [
				`The ticket with ID {1} has expired. it was valid for {2} days from the calculation date`,
				getTicketId(ticket),
				Config.main.ticketPayoutExpirationDays,
			],
		});

	return isTicketPayPeriodExpired;
};
